import React, { useState, useCallback } from "react";
import "./PatientForm.css";
import Select from "react-select";
import makeAnimated from "react-select/animated";
import { AiOutlineClose } from "react-icons/ai";
import Alcohol from "../Assets/Alcohol.png";
import Cigarette from "../Assets/Cigarette.png";
import { useAuth } from "../../AuthProvider";
import api from "./../../axiosInstance";
import Massage from "../MassagePopUp/Massage";
import logger from "../../utils/logger";

// ─── Module-level constants (never recreated) ────────────────────────────────
const animatedComponents = makeAnimated();

const HABIT_OPTIONS = [
  { value: "no", label: "No" },
  { value: "ocasionally", label: "Occasionally (1-2 times a month)" },
  { value: "frequently", label: "Frequently (1-2 times a week)" },
  { value: "daily", label: "Daily" },
];

const MEDICAL_CONDITION_OPTIONS = [
  { value: "diabetes", label: "Diabetes" },
  { value: "hypertension", label: "Hypertension" },
  { value: "asthma", label: "Asthma" },
  { value: "cardiac", label: "Cardiac Issues" },
];

const ALLERGIES_OPTIONS = [
  { value: "pollen", label: "Pollen" },
  { value: "dust", label: "Dust" },
  { value: "peanuts", label: "Peanuts" },
];

const HEREDITY_OPTIONS = [
  { value: "diabetes", label: "Diabetes" },
  { value: "hypertension", label: "Hypertension" },
  { value: "asthma", label: "Asthma" },
];

const INITIAL_FORM = (countryCode, mobileNumber) => ({
  firstName: "",
  middleName: "",
  lastName: "",
  gender: "",
  dateOfBirth: "",
  age: "",
  bloodGroup: "",
  email: "",
  phoneNumber: mobileNumber,
  countryCode: countryCode,
  medicalCondition: [],
  allergies: [],
  heredity: [],
  alcohol: null,
  smoking: null,
});

// ─── Component ────────────────────────────────────────────────────────────────
function PatientForm({ setIsPatientFormOpen, countryCode, mobileNumber, fetchProfiles }) {
  const [formData, setFormData] = useState(() => INITIAL_FORM(countryCode, mobileNumber));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = React.useState('');
  const [successMessage, setSuccessMessage] = React.useState('');

  const onClose = () => {
    setIsPatientFormOpen(false)
  }

  // Single handler for all plain inputs
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    // Only allow digits for weight/age
    if ((name === "weight" || name === "age") && !/^\d*$/.test(value)) return;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  // Single handler for all react-select fields
  const handleSelectChange = useCallback((selectedOption, { name }) => {
    setFormData((prev) => ({ ...prev, [name]: selectedOption }));
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    const { firstName, lastName, dateOfBirth, age, gender, phoneNumber } = formData;

    if (!firstName || !lastName || !gender || !phoneNumber) {
      alert("Please fill all required fields marked with *");
      return;
    }

    if (!dateOfBirth && !age) {
      alert("Please provide either Date of Birth or Age");
      return;
    }

    const requestBody = {
      name: { firstName, lastName },
      contact: {
        phoneNumber: {
          countryCode: countryCode.replace("+", ""),
          phoneNumber: mobileNumber,
        },
        alternatePhoneNumber: [],
        permenantAddress: [],
        currentAddress: [],
      },
      gender,
    };

    if (dateOfBirth) {
      requestBody.dateOfBirth = new Date(dateOfBirth).toISOString();
    } else {
      requestBody.age = parseInt(age, 10);
    }

    if (formData.middleName) requestBody.name.middleName = formData.middleName;
    if (formData.bloodGroup) requestBody.bloodGroup = formData.bloodGroup;
    if (formData.medicalCondition.length > 0) requestBody.chronicConditions = formData.medicalCondition.map((c) => c.value);
    if (formData.allergies.length > 0) requestBody.allergies = formData.allergies.map((c) => c.value);
    if (formData.heredity.length > 0) requestBody.heridityTags = formData.heredity.map((c) => c.value);
    if (formData.alcohol || formData.smoking) {
      requestBody.addictionInformation = {};
      if (formData.alcohol) requestBody.addictionInformation.alcohol = formData.alcohol.value;
      if (formData.smoking) requestBody.addictionInformation.smoking = formData.smoking.value;
    }

    setIsSubmitting(true);
    try {
      logger.debug("Submitting patient data:", requestBody); // Debug log
      await api.post("/patientUser", requestBody);
      fetchProfiles();

      // ✅ Show message FIRST, close AFTER user has seen it
      setSuccessMessage("Patient added successfully. Your next step is to book appointments ✅");
      setTimeout(() => onClose(), 2500); // close after 2.5s

    } catch (error) {
      setErrorMessage("Failed to add patient. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, countryCode, mobileNumber, fetchProfiles, onClose]);

  return (
    <>
      {errorMessage && (<Massage message={errorMessage} clearError={() => setErrorMessage('')} type="error" />)}
      {successMessage && (<Massage message={successMessage} clearError={() => setSuccessMessage('')} type="success" />)}
      <div className="patient-form-overlay">
        <div className="patient-form-container">
          <AiOutlineClose className="patient-form-close-icon" onClick={onClose} />
          <h1 className="patient-form-title">Patient Info</h1>

          <form onSubmit={handleSubmit}>
            {/* Name */}
            <label className="patient-form-label">
              Name <span className="required-star">*</span>
            </label>
            <div className="patient-form-flex-group">
              <input
                type="text"
                name="firstName"
                placeholder="First Name"
                value={formData.firstName}
                onChange={handleChange}
                className="patient-form-input patient-form-firstname"
                required
              />
              <input
                type="text"
                name="middleName"
                placeholder="Middle Name"
                value={formData.middleName}
                onChange={handleChange}
                className="patient-form-input patient-form-middlename"
              />
              <input
                type="text"
                name="lastName"
                placeholder="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                className="patient-form-input patient-form-lastname"
                required
              />
            </div>

            {/* Email */}
            <label className="patient-form-label">Email</label>
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={handleChange}
              className="patient-form-input patient-form-email"
            />

            {/* Contact */}
            <label className="patient-form-label">
              Contact Number <span className="required-star">*</span>
            </label>
            <div className="patient-form-flex-group">
              <select
                name="countryCode"
                value={formData.countryCode}
                onChange={handleChange}
                className="patient-form-select-country-code"
              >
                <option value="+91">India +91</option>
              </select>
              <input
                type="text"
                name="phoneNumber"
                placeholder="Phone Number"
                value={formData.phoneNumber}
                readOnly
                className="patient-form-input patient-form-phone"
                required
              />
            </div>

            {/* Gender / Blood Group / DOB */}
            <div className="patient-form-flex-group">
              <div>
                <label className="patient-form-label">
                  Gender <span className="required-star">*</span>
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="patient-form-bloodgroup"
                  required
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="patient-form-label">Blood Group</label>
                <select
                  name="bloodGroup"
                  value={formData.bloodGroup}
                  onChange={handleChange}
                  className="patient-form-bloodgroup"
                >
                  <option value="">Select</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>  {/* Fixed: was value="AB" for both */}
                  <option value="AB-">AB-</option>  {/* Fixed: was value="AB" for both */}
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>

              <div>
                <label className="patient-form-label">
                  Date of Birth
                </label>
                <input
                  type="date"
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleChange}  /* handleDateChange removed — handleChange covers this */
                  className="patient-form-bloodgroup"
                  disabled={!!formData.age}
                />
              </div>

              <div>
                <label className="patient-form-label">
                  Age
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  name="age"
                  placeholder="Age in years"
                  value={formData.age}
                  onChange={handleChange}
                  className="patient-form-bloodgroup"
                  disabled={!!formData.dateOfBirth}
                />
              </div>
            </div>
            <p className="patient-form-hint">Provide either Date of Birth or Age <span className="required-star">*</span></p>

            {/* Multi-selects */}
            <label className="patient-form-label">Medical Condition</label>
            <Select
              name="medicalCondition"
              value={formData.medicalCondition}
              onChange={handleSelectChange}
              options={MEDICAL_CONDITION_OPTIONS}
              isMulti
              isSearchable
              components={animatedComponents}
              className="slect-for-medication"
            />

            <label className="patient-form-label">Allergies</label>
            <Select
              name="allergies"
              value={formData.allergies}
              onChange={handleSelectChange}
              options={ALLERGIES_OPTIONS}
              isMulti
              isSearchable
              components={animatedComponents}
            />

            <label className="patient-form-label">Heredity</label>
            <Select
              name="heredity"
              value={formData.heredity}
              onChange={handleSelectChange}
              options={HEREDITY_OPTIONS}
              isMulti
              isSearchable
              components={animatedComponents}
            />

            {/* Addictive habits */}
            <div className="patient-form-addictive-habits">
              <label className="patient-form-label">Addictive Habits</label>
              <div className="patient-form-habit-select">
                <img src={Alcohol} alt="Alcohol" className="patient-form-habit-icon" />
                <Select
                  name="alcohol"
                  value={formData.alcohol}
                  options={HABIT_OPTIONS}
                  onChange={handleSelectChange}
                  components={animatedComponents}
                  className="patient-form-habit-addictive"
                />
                <img src={Cigarette} alt="Cigarette" className="patient-form-habit-icon-cigarrete" />
                <Select
                  name="smoking"
                  value={formData.smoking}
                  options={HABIT_OPTIONS}
                  onChange={handleSelectChange}
                  components={animatedComponents}
                  className="patient-form-habit-addictive"
                />
              </div>
            </div>

            <button
              type="submit"
              className="patient-form-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting…" : "Add Patient"}
            </button>
          </form>
        </div>
      </div>
    </>
  );
}

export default PatientForm;